import { useEffect, useRef, useState } from "react";
import { useReload } from "../../hooks";
import { MapRouteProps } from "../../components/framework/map/map";

interface UseMapZoomParams {
    workShiftId?: string;
    selectedWorkShiftTimeSlotId: string;
    selectedVehicleId: string | null;
    isVehicleDetailView: boolean;
    isWorkShiftView: boolean;
    isVehicleRouteView: boolean;
    isVehicleShiftTabView: boolean;
    vehicleRoute: unknown[] | null;
    workshiftTimeSlotRoutes: MapRouteProps[];
    workshiftPoints: unknown[];
    selectedWorkTimeTypeIds: (string | null)[];
    showNoWorkTime: boolean;
    dataLength: number;
    mapDataLoading: boolean;
    vehicleRouteLoading: boolean;
    reloadKey: number;
    reloadWorkShiftData: () => void;
}

export const useMapZoom = ({
    workShiftId,
    selectedWorkShiftTimeSlotId,
    selectedVehicleId,
    isVehicleDetailView,
    isWorkShiftView,
    isVehicleRouteView,
    isVehicleShiftTabView,
    vehicleRoute,
    workshiftTimeSlotRoutes,
    workshiftPoints,
    selectedWorkTimeTypeIds,
    showNoWorkTime,
    dataLength,
    mapDataLoading,
    vehicleRouteLoading,
    reloadKey,
    reloadWorkShiftData,
}: UseMapZoomParams) => {
    const [workShiftPositionsLoading, setWorkShiftPositionsLoading] =
        useState(false);
    const [zoomKey, zoom] = useReload();
    const shouldZoom = useRef(false);

    // Reload work shift map data when reloadKey changes
    useEffect(() => {
        reloadWorkShiftData();
    }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // When a new work shift is selected, start loading and prepare to zoom
    useEffect(() => {
        if (workShiftId) {
            setWorkShiftPositionsLoading(true);
            shouldZoom.current = true;
        }
    }, [workShiftId]);

    // Zoom when a specific time slot is selected
    useEffect(() => {
        if (selectedWorkShiftTimeSlotId) {
            zoom();
        }
    }, [selectedWorkShiftTimeSlotId, zoom]);

    // Zoom when work shift is cleared
    useEffect(() => {
        if (!workShiftId) {
            zoom();
        }
    }, [workShiftId, zoom]);

    // Zoom when work shift routes and points have finished loading
    useEffect(() => {
        if (
            shouldZoom.current &&
            workshiftTimeSlotRoutes?.length > 0 &&
            workshiftPoints?.length > 0
        ) {
            setWorkShiftPositionsLoading(false);
            zoom();
            shouldZoom.current = false;
        }
    }, [workshiftTimeSlotRoutes, workshiftPoints, zoom]);

    // Zoom when work time type filters change (if pending)
    useEffect(() => {
        if (shouldZoom.current && selectedWorkTimeTypeIds) {
            zoom();
            shouldZoom.current = false;
        }
    }, [selectedWorkTimeTypeIds, showNoWorkTime, zoom]);

    // Zoom when selected vehicle changes
    useEffect(() => {
        zoom();
    }, [selectedVehicleId, zoom]);

    // Re-center map when filtered data changes (e.g. vehicle status/group filters)
    useEffect(() => {
        if (!isVehicleDetailView && !isWorkShiftView) {
            zoom();
        }
    }, [dataLength, isVehicleDetailView, isWorkShiftView, zoom]);

    // Sync loading state from work shift map data
    useEffect(() => {
        setWorkShiftPositionsLoading(mapDataLoading);
    }, [mapDataLoading]);

    // Sync loading state from vehicle route data
    useEffect(() => {
        if (isVehicleShiftTabView || isVehicleRouteView) {
            setWorkShiftPositionsLoading(vehicleRouteLoading);
        }
    }, [vehicleRouteLoading, isVehicleShiftTabView, isVehicleRouteView]);

    // Zoom when vehicle route data arrives
    useEffect(() => {
        if (vehicleRoute?.length > 0) {
            zoom();
        }
    }, [vehicleRoute, isVehicleRouteView, isVehicleDetailView, isVehicleShiftTabView, zoom]);

    // Zoom when entering work shift view
    useEffect(() => {
        if (isWorkShiftView) {
            zoom();
        }
    }, [isWorkShiftView, zoom]);

    return { zoomKey, zoom, workShiftPositionsLoading };
};
